import React, { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Activity, ArrowRight, Clock, AlertCircle } from "lucide-react";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, LineChart, Line, ReferenceLine,
} from "recharts";
import { Card, Badge, SectionLabel, Divider } from "../components/ui-bits";
import { Skeleton } from "../components/ui/skeleton";
import { globalEvents as fallbackEvents } from "../data/mockData";
import { liveAPI } from "../data/liveApi";
import { usePoll } from "../hooks/usePoll";
import { SeoHead } from "../seo/SeoHead";

const GREEN = "#00FF88";
const RED = "#FF453A";
const GREY = "#71717A";

function chartColor(current, prev) {
  if (!prev || !current) return GREY;
  const diff = current - prev;
  const eps = Math.max(prev * 0.0001, 0.01);   // treat very small diffs as flat
  if (diff > eps) return GREEN;
  if (diff < -eps) return RED;
  return GREY;
}

// Stable chart configuration (extracted to avoid re-creating on every render)
const SPARKLINE_MARGIN = { top: 4, right: 0, left: 0, bottom: 0 };
const CHART_MARGIN = { top: 6, right: 12, left: 0, bottom: 0 };
const PREV_LINE_STROKE = "#8A8A8A";
const TOOLTIP_CONTENT_STYLE = {
  background: "#121212",
  border: "1px solid #272727",
  borderRadius: 2,
  fontFamily: "Inter, system-ui, sans-serif",
  fontSize: 11,
  color: "#F3F4F6",
};

const Sparkline = ({ series, positive }) => {
  if (!series || series.length === 0) return <div className="h-10" />;
  const color = positive ? GREEN : RED;
  return (
    <div className="h-10 -mx-1 mt-2">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={SPARKLINE_MARGIN}>
          <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.4} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const RANGES = ["1D", "1W", "1M", "3M", "YTD"];

const NiftyChart = ({ symbol = "NIFTY 50" }) => {
  const [range, setRange] = useState("1D");
  const { data, loading } = usePoll(
    () => liveAPI.history(symbol, range),
    range === "1D" ? 20_000 : 5 * 60_000,
    [symbol, range],
  );

  const series = data?.series || [];
  const prev = data?.prev_close || 0;
  const current = data?.current || (series.at?.(-1)?.v ?? 0);
  const color = chartColor(current, prev);
  const positive = color !== RED;
  const change = current && prev ? current - prev : 0;
  const pct = prev ? (change / prev) * 100 : 0;
  const gradKey = color === GREEN ? "g" : color === RED ? "r" : "n";

  // Y-axis domain that ALWAYS includes the prev-close so the reference line stays
  // visible on every timeframe (including gap-up/down 1D sessions).
  const yDomain = useMemo(
    () => (prev > 0
      ? [(dataMin) => Math.min(dataMin, prev), (dataMax) => Math.max(dataMax, prev)]
      : ["dataMin", "dataMax"]),
    [prev]
  );

  const tooltipCursor = useMemo(
    () => ({ stroke: color, strokeOpacity: 0.5, strokeDasharray: "2 2" }),
    [color]
  );
  const tooltipLabelFormatter = useMemo(
    () => (l) => `${range === "1D" ? "IST " : ""}${l}`,
    [range]
  );
  const tooltipValueFormatter = useMemo(
    () => (v) => {
      const diff = v - prev;
      const dpct = prev ? (diff / prev) * 100 : 0;
      return [
        `${v.toFixed(2)}  Δ ${diff >= 0 ? "+" : ""}${diff.toFixed(2)} (${dpct >= 0 ? "+" : ""}${dpct.toFixed(2)}%)`,
        symbol,
      ];
    },
    [prev, symbol]
  );

  return (
    <Card className="lg:col-span-2">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <SectionLabel>MARKET SNAPSHOT</SectionLabel>
          <div className="mt-1 flex items-baseline gap-3 flex-wrap">
            <span className="fij-mono text-2xl sm:text-3xl text-fij-text">
              {current ? current.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
            </span>
            <span className="font-mono text-sm" style={{ color }}>
              {change >= 0 ? "+" : ""}{change.toFixed(2)} ({pct >= 0 ? "+" : ""}{pct.toFixed(2)}%)
            </span>
          </div>
          <div className="font-mono text-[10px] text-fij-textMuted mt-1">
            {symbol} · {range} · Prev Close: {prev ? prev.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
          </div>
        </div>
        <div className="flex items-center gap-1 font-mono text-[10px] flex-wrap">
          {RANGES.map((r) => (
            <button
              key={r}
              data-testid={`range-${r}`}
              onClick={() => setRange(r)}
              className={`px-2 py-1 rounded-sm border transition-colors ${
                r === range
                  ? "bg-fij-green/10 border-fij-green/40 text-fij-green"
                  : "border-fij-border text-fij-textDim hover:text-fij-green hover:border-fij-green/40"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <div className="h-56 sm:h-64 -mx-2 relative">
        {loading && !series.length && (
          <div className="absolute inset-0 grid place-items-center font-mono text-xs text-fij-textMuted">
            Loading {range}…
          </div>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series} margin={CHART_MARGIN}>
            <defs>
              <linearGradient id={`grad-${gradKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.32} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="label" hide />
            <YAxis hide domain={yDomain} />
            <Tooltip
              contentStyle={TOOLTIP_CONTENT_STYLE}
              cursor={tooltipCursor}
              labelFormatter={tooltipLabelFormatter}
              formatter={tooltipValueFormatter}
            />
            <Area
              type="monotone"
              dataKey="v"
              stroke={color}
              strokeWidth={1.6}
              fill={`url(#grad-${gradKey})`}
              isAnimationActive={false}
            />
            {/* Prev-close reference line drawn AFTER Area so it renders on top. */}
            {prev > 0 && (
              <ReferenceLine
                y={prev}
                stroke={PREV_LINE_STROKE}
                strokeOpacity={0.6}
                strokeDasharray="4 4"
                strokeWidth={1}
                ifOverflow="extendDomain"
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

const MarketStats = ({ symbol }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    liveAPI.stats(symbol)
      .then((d) => { if (mounted) setData(d); })
      .catch(() => { if (mounted) setData(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [symbol]);

  const fmt = (v, digits = 2) =>
    typeof v === "number" ? v.toLocaleString("en-IN", { maximumFractionDigits: digits, minimumFractionDigits: digits }) : "—";

  const rows = [
    { label: "Today's High",   value: fmt(data?.day_high) },
    { label: "Today's Low",    value: fmt(data?.day_low) },
    { label: "52 Week High",   value: fmt(data?.fifty_two_week_high) },
    { label: "52 Week Low",    value: fmt(data?.fifty_two_week_low) },
    { label: "Previous Close", value: fmt(data?.previous_close) },
  ];

  return (
    <Card data-testid="market-stats">
      <div className="flex items-center justify-between mb-3">
        <SectionLabel>MARKET STATISTICS · {symbol}</SectionLabel>
        <span className="font-mono text-[10px] text-fij-textMuted">
          {data?.currency || "INR"} · live
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {rows.map((r) => (
          <div key={r.label} data-testid={`stat-${r.label.replace(/\s+|\W/g, "-").toLowerCase()}`}>
            <div className="font-mono text-[10px] text-fij-textMuted uppercase">{r.label}</div>
            {loading ? (
              <Skeleton className="h-5 w-24 mt-1 bg-fij-border/40" />
            ) : (
              <div className="font-mono text-sm text-fij-text mt-0.5">{r.value}</div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};

export default function Dashboard({ seoPath = "/dashboard" }) {
  const { data: statusData } = usePoll(liveAPI.marketStatus, 1000, []);
  const marketOpen = statusData?.is_open ?? false;
  // Refresh cadence: 15s during Indian market hours, 60s outside.
  const indicesInterval = marketOpen ? 15_000 : 60_000;
  const stripInterval   = marketOpen ? 15_000 : 60_000;

  const { data: indicesData } = usePoll(liveAPI.indices, indicesInterval, [marketOpen]);
  const { data: stripData }   = usePoll(liveAPI.marketStrip, stripInterval, [marketOpen]);
  const { data: newsData }    = usePoll(() => liveAPI.news(), 60_000, []);

  // Chart symbol — driven by ?symbol= (from global search) or by KPI card clicks.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSymbol = searchParams.get("symbol");
  const validSymbols = ["NIFTY 50", "SENSEX", "NIFTY MIDCAP 100", "NIFTY SMALLCAP 100", "BANK NIFTY"];
  const [chartSymbol, setChartSymbol] = useState(
    validSymbols.includes(urlSymbol) ? urlSymbol : "NIFTY 50"
  );
  useEffect(() => {
    if (urlSymbol && validSymbols.includes(urlSymbol) && urlSymbol !== chartSymbol) {
      setChartSymbol(urlSymbol);
    }
  }, [urlSymbol]);
  const selectChartSymbol = (s) => {
    setChartSymbol(s);
    setSearchParams(s === "NIFTY 50" ? {} : { symbol: s }, { replace: true });
  };

  const now = { time: statusData?.ist_time ?? "—:—:—", weekday: statusData?.weekday ?? "—" };

  // Persist last-known-good indices across refetches so brief 4xx/5xx don't cause flicker.
  const [lastGoodIndices, setLastGoodIndices] = useState(null);
  const [lastGoodStrip, setLastGoodStrip]     = useState(null);

  useEffect(() => {
    const live = indicesData?.indices || [];
    if (live.some((i) => i.value !== "—")) setLastGoodIndices(live);
  }, [indicesData]);

  useEffect(() => {
    const strip = stripData?.indicators || [];
    if (strip.some((i) => i.value !== "—")) setLastGoodStrip(strip);
  }, [stripData]);

  const indicesLoaded = lastGoodIndices !== null;
  const stripLoaded   = lastGoodStrip !== null;

  const indices = lastGoodIndices || [];
  const stripIndicators = lastGoodStrip || [];

  // Only show the "unavailable" banner if we've received a response AND every symbol is unavailable.
  const allIndicesFailed =
    indicesData !== null &&
    (indicesData?.indices || []).length > 0 &&
    (indicesData?.indices || []).every((i) => i.value === "—");

  const find = (kw) => indices.find((i) => i.ticker.toUpperCase().includes(kw)) || {};
  const nifty  = find("NIFTY 50");
  const sensex = find("SENSEX");
  const midcap = find("MIDCAP");
  const small  = find("SMALLCAP");

  const liveNews = newsData?.news || [];
  const recentItems = liveNews.length
    ? liveNews.slice(0, 5).map((n, i) => ({
        id: n.id || `n-${i}`, title: n.headline,
        severity: n.impact >= 7 ? "high" : n.impact >= 5 ? "medium" : "low",
        asset: n.category,
        time: n.published_at ? new Date(n.published_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—",
      }))
    : fallbackEvents.slice(0, 5);

  return (
    <div className="space-y-6">
      <SeoHead path={seoPath} />
      {allIndicesFailed && (
        <Card data-testid="data-source-banner" className="flex items-start gap-3 border-fij-amber/40 bg-fij-amber/5">
          <AlertCircle size={16} className="text-fij-amber shrink-0 mt-0.5" />
          <div className="font-mono text-xs text-fij-amber">
            <div className="font-medium">Live indices unavailable — provider chain exhausted.</div>
            <div className="text-fij-textDim mt-0.5">Add <span className="text-fij-text">TWELVE_DATA_API_KEY</span> in <span className="text-fij-text">/app/backend/.env</span> for a primary feed.</div>
          </div>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="kpi-grid">
        <Card data-testid="kpi-market-status">
          <SectionLabel>MARKET STATUS</SectionLabel>
          <div className="mt-2 flex items-baseline gap-2">
            <span data-testid="market-status-value" className={`fij-mono text-xl sm:text-2xl ${marketOpen ? "text-fij-green" : "text-fij-red"}`}>
              {marketOpen ? "OPEN" : "CLOSED"}
            </span>
            <span className={`w-2 h-2 rounded-full ${marketOpen ? "bg-fij-green animate-pulse-green" : "bg-fij-red"}`} />
          </div>
          <div className="mt-2 font-mono text-[11px] text-fij-textDim space-y-0.5">
            <div>Exchange · <span className="text-fij-text">NSE & BSE</span></div>
            <div>Hours · <span className="text-fij-text">09:15 – 15:30 IST</span></div>
            <div className="flex items-center gap-1">
              <Clock size={10} /> <span data-testid="ist-time" className="text-fij-text">{now.time}</span> IST
            </div>
          </div>
        </Card>

        {[
          { tid: "kpi-nifty",    title: "NIFTY 50",            i: nifty,  symbol: "NIFTY 50"            },
          { tid: "kpi-sensex",   title: "SENSEX",              i: sensex, symbol: "SENSEX"              },
          { tid: "kpi-midcap",   title: "NIFTY MIDCAP 100",    i: midcap, symbol: "NIFTY MIDCAP 100"    },
          { tid: "kpi-smallcap", title: "NIFTY SMALLCAP 100",  i: small,  symbol: "NIFTY SMALLCAP 100"  },
        ].map((c) => {
          if (!indicesLoaded) {
            return (
              <Card key={c.tid} data-testid={`${c.tid}-skeleton`}>
                <div className="flex items-center justify-between">
                  <SectionLabel>{c.title}</SectionLabel>
                  <Skeleton className="h-4 w-14 bg-fij-border/40" />
                </div>
                <Skeleton className="h-8 w-28 mt-3 bg-fij-border/40" />
                <Skeleton className="h-3 w-20 mt-2 bg-fij-border/40" />
              </Card>
            );
          }
          const color = c.i.positive ? "text-fij-green" : "text-fij-red";
          const active = chartSymbol === c.symbol;
          return (
            <button
              key={c.tid}
              data-testid={c.tid}
              data-active={active || undefined}
              onClick={() => selectChartSymbol(c.symbol)}
              className={`fij-card rounded-sm p-4 sm:p-5 text-left transition-colors cursor-pointer w-full ${
                active ? "border-fij-green/60 bg-fij-green/[0.03]" : "hover:border-fij-green/30"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <SectionLabel>{c.title}</SectionLabel>
                <Badge tone={c.i.positive ? "positive" : "negative"}>{c.i.change_pct || "—"}</Badge>
              </div>
              <div className="mt-2 fij-mono text-xl sm:text-2xl text-fij-text">{c.i.value || "—"}</div>
              <div className={`font-mono text-xs mt-1 ${color}`}>{c.i.change || "—"}</div>
              {active && <div className="mt-2 font-mono text-[10px] text-fij-green">▼ CHARTED</div>}
            </button>
          );
        })}
      </div>

      {/* Nifty 50 chart + recent events */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <NiftyChart symbol={chartSymbol} />

        <Card className="flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <SectionLabel>RECENT EVENTS</SectionLabel>
            <Link to="/events" data-testid="events-see-all" className="font-mono text-[10px] text-fij-textDim hover:text-fij-green flex items-center gap-1">
              SEE ALL <ArrowRight size={10} />
            </Link>
          </div>
          <div className="space-y-2 flex-1">
            {recentItems.map((e) => (
              <Link
                key={e.id}
                to={e.id?.startsWith?.("evt-") ? `/events/${e.id}` : "/events"}
                data-testid={`recent-event-${e.id}`}
                className="block border border-fij-border rounded-sm p-3 hover:border-fij-green/40 hover:bg-fij-surfaceHover transition-colors group"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge tone={e.severity === "high" ? "warning" : e.severity === "medium" ? "info" : "neutral"}>{e.severity}</Badge>
                      <span className="font-mono text-[10px] text-fij-textMuted">{e.asset} · {e.time}</span>
                    </div>
                    <div className="text-sm text-fij-text line-clamp-2 leading-snug">{e.title}</div>
                  </div>
                  <ArrowUpRight size={14} className="text-fij-textMuted group-hover:text-fij-green shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        </Card>
      </div>

      {/* Market Statistics for the currently-charted index */}
      <MarketStats symbol={chartSymbol} />

      {/* Bottom market strip: Gold / Silver / USDINR / S&P 500 / Dow / Nikkei */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <SectionLabel>GLOBAL INDICATORS</SectionLabel>
          <span className="font-mono text-[10px] text-fij-textMuted">
            refreshes every {marketOpen ? "15s" : "60s"}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="market-strip">
          {!stripLoaded
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} data-testid={`strip-skeleton-${i}`} className="border-l-2 border-fij-border pl-2">
                  <Skeleton className="h-3 w-16 bg-fij-border/40" />
                  <Skeleton className="h-4 w-20 mt-1 bg-fij-border/40" />
                  <Skeleton className="h-3 w-14 mt-1 bg-fij-border/40" />
                </div>
              ))
            : stripIndicators.map((i) => {
                const tid = (i.ticker || "").replace(/\s+/g, "-").replace(/\//g, "-");
                const unavailable = i.source === "unavailable";
                const color = unavailable
                  ? "text-fij-textMuted"
                  : (i.positive ? "text-fij-green" : "text-fij-red");
                const borderColor = unavailable
                  ? "border-fij-border"
                  : (i.positive ? "border-fij-green" : "border-fij-red");
                return (
                  <div
                    key={i.ticker}
                    data-testid={`strip-${tid}`}
                    className={`border-l-2 ${borderColor} pl-2`}
                    title={unavailable ? (i.note || "Unavailable on free feed") : undefined}
                  >
                    <div className="font-mono text-[10px] text-fij-textMuted truncate">
                      {i.ticker}{i.unit ? <span className="text-fij-textMuted/70"> · {i.unit}</span> : null}
                    </div>
                    <div className="font-mono text-sm text-fij-text mt-0.5">{i.value}</div>
                    <div className={`font-mono text-[11px] ${color}`}>
                      {i.change} · {i.change_pct}
                    </div>
                  </div>
                );
              })}
        </div>
      </Card>

      <Card className="flex items-center gap-3 text-xs font-mono text-fij-textDim flex-wrap">
        <Activity size={14} className="text-fij-green" />
        <span>Live data · NSE + BSE feeds via Yahoo / Twelve Data · {indicesLoaded ? "LIVE" : "LOADING"}</span>
        <span className="ml-auto">{now.weekday} · {now.time} IST</span>
      </Card>
    </div>
  );
}
